import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { css, Theme, useTheme } from "@emotion/react";

import { NOOP } from "core/constants";
import ErrorIcon from "./ErrorIcon";

type Props = {
    hasError?: boolean,
    id?: string,
    innerRef?: React.RefCallback<HTMLInputElement>|React.Ref<HTMLInputElement>,
    name: string,
    label?: string,
    onChange?: any,
    onEventChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
    value: any,
    hasBorder?: boolean,
    hasIconOnError?: boolean,
    [x: string]: any,
    selectOptions: string[]|undefined,
    setSelectedOption: any,
    clearSelectOptions: () => void,
};

const InputWithSelectOptions: React.FC<Props> = (
    {
        hasError = false,
        id = "",
        innerRef = undefined,
        name,
        onChange = NOOP,
        onEventChange = NOOP,
        value,
        label = "",
        hasBorder = true,
        hasIconOnError = true,
        selectOptions,
        setSelectedOption,
        clearSelectOptions,
    }
) => {
    const theme: Theme = useTheme();
    const [active, setActive] = useState(false);
    const dropdownRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setActive(!!selectOptions?.length);
    }, [selectOptions, setActive]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                clearSelectOptions();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [clearSelectOptions, dropdownRef]);

    // It changes the input value to the clicked text.
    const setSelectOptionHandler = useCallback((selectedTarget: any) => {
        setSelectedOption(selectedTarget);
        clearSelectOptions();
    }, [setSelectedOption, clearSelectOptions]);

    const handleChange = useCallback((e: any) => {
        if (onChange) {
            onChange(name, e.target.value);
        }

        if (onEventChange) {
            e.persist();
            onEventChange(e);
        }
    }, [name, onChange, onEventChange]);

    const styles = {
        container: css`
            position: relative;
            ${theme.forms.defaults};
        `,
        selectOptionContainer: css`
            position: absolute;
            padding: 8px 10px;
            width: 100%;
            border-radius: 0 0 9px 9px;
            box-shadow: ${theme.borderAndShadow.boxShadow};
            background-color: white;
            z-index: ${theme.zIndex.zIndexThree};

            div {
                padding: 5px 0 5px 15px;
                font-weight: ${theme.fonts.weights.bold};

                span {
                    text-decoration: underline;
                    cursor: pointer;
                }
            }
        `,
        input: css`
            border: 0;
            border-bottom: 3px solid ${hasBorder
                    ? (selectOptions?.length ? theme.colours.red1 : theme.forms.defaults.borderColor)
                    : theme.colours.transparent};
            border-radius: 0;
            height: 50px;
            background: transparent;
            outline: none;
            width: 100%;
            font-size: ${theme.forms.label.fontSize};
            font-family: ${theme.fonts.frutiger};
            font-weight: ${theme.fonts.weights.light};
            ${hasError && theme.forms.error};
            ${theme.mixins.placeholder()};
        `,
    };

    return (
        <div css={styles.container} ref={dropdownRef}>
            <input
                css={styles.input}
                id={id || name}
                name={name}
                ref={innerRef}
                value={value || ''}
                onChange={handleChange}
                placeholder={label}
                type="text"
            />
            {hasError && hasIconOnError && (
                <ErrorIcon />
            )}
            {active && (
                <div css={styles.selectOptionContainer}>
                    Did you mean
                    {selectOptions?.map((item, index) => (
                        <div key={`input-select-option-${index}`}>
                            <span onClick={() => setSelectOptionHandler(item)}>{item}</span>?
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(InputWithSelectOptions);